import React from 'react';
import HomeLink from './HomeLink';
import { DISPLAY_FONT, FOCUS_RING } from './tokens';

/**
 * HOME-01 — the header every discovery row wears: title, an optional info dot,
 * and "View all →".
 *
 * One component rather than five near-identical flex rows, because the three
 * things that vary between them (heading level, size, whether there is a
 * tooltip) are exactly the three things a reviewer would otherwise have to
 * check five times.
 */

export type RowHeaderLevel = 'section' | 'row';

interface RowHeaderProps {
  title: string;
  /** Renders the design's small "i" dot. The string IS the tooltip. */
  tooltip?: string;
  /** Omitted → no "View all" link at all, not a dead one. */
  viewAllHref?: string;
  /** Defaults to the design's "View all →". */
  viewAllLabel?: string;
  /**
   * `section` = the 26–32px H2 above a whole block (Spotlight, Discover).
   * `row`     = the 19px H3 above one carousel inside Discover.
   */
  level?: RowHeaderLevel;
  className?: string;
}

const RowHeader: React.FC<RowHeaderProps> = ({
  title,
  tooltip,
  viewAllHref,
  viewAllLabel = 'View all →',
  level = 'row',
  className = '',
}) => {
  const Heading = level === 'section' ? 'h2' : 'h3';

  return (
    <div className={`flex items-baseline gap-2.5 ${className}`}>
      <Heading
        className={
          level === 'section'
            ? 'm-0 text-[19px] font-extrabold tracking-[-0.02em] text-[#2D2D2D] lg:text-[26px]'
            : 'm-0 text-[19px] font-bold tracking-[-0.01em] text-[#2D2D2D]'
        }
        style={{ fontFamily: DISPLAY_FONT }}
      >
        {title}
      </Heading>

      {tooltip && (
        <span
          title={tooltip}
          aria-label={tooltip}
          role="note"
          className="inline-flex h-[15px] w-[15px] flex-none cursor-help select-none items-center justify-center self-center rounded-full border-[1.5px] border-[#A8A491] text-[10px] font-bold text-[#666666] lg:h-[17px] lg:w-[17px] lg:text-[11px]"
        >
          i
        </span>
      )}

      {viewAllHref && (
        <HomeLink
          to={viewAllHref}
          className={`ml-auto flex-none rounded text-[12px] font-semibold text-[#134025] hover:text-[#1B5533] hover:underline lg:text-[13.5px] ${FOCUS_RING}`}
        >
          {viewAllLabel}
        </HomeLink>
      )}
    </div>
  );
};

export default RowHeader;
